import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import {
  Box,
  MenuItem,
  Typography,
  FormControl,
  CircularProgress,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Popover,
  Paper,
  MenuList,
  ClickAwayListener,
  Select,
  InputLabel,
  Alert,
} from '@mui/material';
import { useAuthContext } from 'src/auth/hooks';
import { useTenant } from 'src/auth/context/firebase/TenantContext';

const TenantDataDisplay = ({ tenantId }) => {
  const [tenantData, setTenantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthContext();

  useEffect(() => {
    const fetchTenantData = async () => {
      try {
        if (tenantId && user) {
          const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/${tenantId}`, {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
            },
          });
          setTenantData(response.data);
        }
      } catch (error) {
        console.error('Error fetching tenant data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, [tenantId, user]);

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      {tenantData ? (
        <pre>{JSON.stringify(tenantData, null, 2)}</pre>
      ) : (
        <Typography variant="body1">No data available for the selected tenant.</Typography>
      )}
    </Box>
  );
};

TenantDataDisplay.propTypes = {
  tenantId: PropTypes.string.isRequired,
};

const TenantSelector = ({ onSelectTenant }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const { user } = useAuthContext();
  const { selectedTenant, setSelectedTenant } = useTenant();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [pendingTenant, setPendingTenant] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (user) {
          const userId = user.uid;
          const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/${userId}`, {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
            },
          });
          const userDataResponse = response.data;
          setUserData(userDataResponse);
          setTenants(userDataResponse.tenants);
          setSelectedTenant(userDataResponse.selectedTenantId || '');
        }
      } catch (error) {
        setFetchError('Error');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user, setSelectedTenant]);

  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    if (term) {
      const matches = tenants.filter((tenant) => tenant.toLowerCase() === term.toLowerCase());
      setFilteredTenants(matches);
    } else {
      setFilteredTenants([]);
    }
  };

  const handleTenantChange = (tenantId) => {
    setPendingTenant(tenantId);
    setOpen(true);
    setAnchorEl(null);
  };

  const handleConfirmSwitch = async () => {
    if (user && pendingTenant) {
      const userId = user.uid;
      try {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/users/${userId}`,
          {
            id: userData.id,
            name: userData.name,
            selectedTenantId: pendingTenant,
            tenants: userData.tenants,
            role: user.role,
          },
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
        setSelectedTenant(pendingTenant);
        onSelectTenant(pendingTenant);
      } catch (updateError) {
        setFetchError('Error updating user data');
        console.error('Error updating user data:', updateError.response ? updateError.response.data : updateError.message);
      }
    }
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopper = () => {
    setAnchorEl(null);
  };

  const renderContent = () => {
    if (loading) {
      return <CircularProgress size={24} />;
    }

    if (fetchError) {
      return <Alert severity="error">{fetchError}</Alert>;
    }

    if (tenants.length === 1) {
      return <Typography variant="body1">{tenants[0]}</Typography>;
    }

    return (
      <FormControl variant="outlined" size="small" fullWidth>
        {userData && userData.role === 'accountManager' ? (
          <Box>
            <Button onClick={handleClick}>{selectedTenant || 'Select Client'}</Button>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleClosePopper}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClosePopper}>
                  <MenuList>
                    <TextField
                      placeholder="Search location"
                      fullWidth
                      value={searchTerm}
                      onChange={handleSearchChange}
                      autoFocus
                    />
                    {filteredTenants.map((tenant, index) => (
                      <MenuItem key={index} onClick={() => handleTenantChange(tenant)}>
                        {tenant}
                      </MenuItem>
                    ))}
                    {filteredTenants.length === 0 && searchTerm && (
                      <MenuItem disabled>No matches found</MenuItem>
                    )}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Popover>
          </Box>
        ) : (
          <Box>
           {/* //<InputLabel id="tenant-select-label">Location</InputLabel>  */}
            <Select
              labelId="tenant-select-label"
              value={selectedTenant}
              onChange={(e) => handleTenantChange(e.target.value)}
              fullWidth
            >
              {tenants.map((tenant) => (
                <MenuItem key={tenant} value={tenant}>
                  {tenant}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </FormControl>
    );
  };

  return (
    <Box>
      {renderContent()}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Confirm Tenant Switch</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to switch to tenant &quot;{pendingTenant}&quot;?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmSwitch} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

TenantSelector.propTypes = {
  onSelectTenant: PropTypes.func.isRequired,
};

const TenantManager = () => {
  const { selectedTenant, setSelectedTenant } = useTenant();

  useEffect(() => {
    const fetchTenantData = async () => {
      // Fetch data based on selectedTenant if needed
    };

    fetchTenantData();
  }, [selectedTenant]);

  return (
    <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
      <TenantSelector onSelectTenant={(tenantId) => setSelectedTenant(tenantId)} />
      {/* <TenantDataDisplay tenantId={selectedTenant} /> */}
    </Box>
  );
};

export default TenantManager;
